import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Globe2, Plus } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";

interface CountryRow {
  id: string;
  countryCode: string;
  countryName: string;
  visaPolicy: string | null;
  arrivalCard: string | null;
  isActive: boolean;
  updatedAt: string;
}

const VISA_LABEL: Record<string, string> = {
  visa_free: "Visa-free",
  visa_on_arrival: "On arrival",
  e_visa: "E-visa",
  embassy: "Embassy",
};

/**
 * Country knowledge base list (Block 5.6 Řez 1). One source feeding the offer PDF,
 * pre-departure pack, portal checklist, arrival cards and the AI sales layer.
 */
export default function CountryKbPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["country-kb"],
    queryFn: () => api.get<ApiResponse<CountryRow[]>>("/api/admin/country-requirements"),
  });
  const rows = data?.data || [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Globe2 className="h-6 w-6 text-gray-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Country KB</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Visa, passport, arrival-card and seasonality facts — feeds offers, the pre-departure
              pack and the AI sales layer.
            </p>
          </div>
        </div>
        <Link to="/country-kb/new" className="btn-primary">
          <Plus className="h-5 w-5" /> New country
        </Link>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="px-4 py-3">Country</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Visa</th>
              <th className="px-4 py-3">Arrival card</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  No countries yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/country-kb/${r.id}`}
                      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                    >
                      {r.countryName}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-gray-500">{r.countryCode}</td>
                  <td className="px-4 py-2 text-gray-500">
                    {r.visaPolicy ? (VISA_LABEL[r.visaPolicy] ?? r.visaPolicy) : "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{r.arrivalCard ?? "—"}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        r.isActive
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {r.isActive ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-400">
                    {new Date(r.updatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
